import { useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Avatar } from "@/components/common";
import { roleLabel, brl } from "@/lib/format";
import { Loader2, Camera } from "@/components/icons";

const INPUT = "w-full h-11 px-3.5 rounded-lg border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40";
const LABEL = "block text-sm font-medium text-slate-700 mb-1.5";

export default function Perfil() {
  const { user, setUser, refreshUser } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Máscara BR: "(31) 98865-1453" para 11 dígitos; "(31) 3123-4567" para 10.
  // Armazenamos no state apenas os dígitos (compatível com o backend).
  const maskPhone = (digits) => {
    const d = (digits || "").replace(/\D/g, "").slice(0, 11);
    if (d.length <= 2) return d.length ? `(${d}` : "";
    if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/me", form);
      setUser(data);
      toast.success("Perfil atualizado");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/auth/avatar", fd);
      setUser(data);
      toast.success("Foto atualizada");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div className="max-w-xl">
      <PageHeader title="Meu perfil" />
      <div className="bg-card rounded-2xl border border-black/5 p-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
        <div className="flex items-center gap-4">
          <div className="relative group">
            <Avatar name={user?.name} src={user?.avatar} size={72} />
            <label className="absolute inset-0 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" data-testid="upload-avatar-btn">
              {uploading ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Camera className="w-5 h-5 text-white" />}
              <input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} disabled={uploading} />
            </label>
          </div>
          <div>
            <div className="text-xl font-semibold text-navy">{user?.name}</div>
            <div className="text-sm text-muted-foreground">{roleLabel(user?.role)} · {user?.email}</div>
            {user?.goal != null && <div className="text-xs text-muted-foreground mt-1">Meta: {brl(user.goal)}</div>}
          </div>
        </div>

        <form onSubmit={save} className="mt-6 space-y-4">
          <div><label className={LABEL}>Nome</label><input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={INPUT} data-testid="perfil-name" /></div>
          <div><label className={LABEL}>Telefone</label><input value={maskPhone(form.phone)} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 11) }))} className={INPUT} data-testid="perfil-phone" inputMode="tel" placeholder="(31) 98865-1453" /></div>
          <button type="submit" disabled={saving} data-testid="save-perfil-btn" className="h-11 px-6 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover flex items-center gap-2 disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Salvar alterações
          </button>
        </form>
      </div>
    </div>
  );
}
